/* ─────────────────────────────────────────────────────────────────────────
   KIMDYMA · eb-reviews.js — the review wall, and the matching that opens it.

   THE THEME OWNS THE SHAPE, THE APP OWNS THE DATA. This file asks the shop's
   review app for a page of reviews and draws them in the theme's own card. It
   never invents a number and never averages anything: every figure it prints
   came back from the endpoint or from Shopify's own metafields.

   THE MATCHING IS THE POINT. A product page knows two things no review app
   can: the variant selected in the picker, and — if the visitor has used the
   fit finder — how tall they are. So the wall opens on the reviews written by
   people that size, on that frame, and SAYS SO: the sentence names what was
   applied and why, both numbers are on screen at all times, and one press
   takes it off. A wall quietly showing 27 of 486 reads as a bike with 27
   reviews, which is worse than not matching at all.

   With no endpoint, or with this file blocked, the section shows its heading
   and the shop's score and nothing else — never an empty grid, never a
   spinner that runs forever.
   ───────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';
  if (window.ebReviewsLoaded) return;
  window.ebReviewsLoaded = true;

  var slice = function (l) { return Array.prototype.slice.call(l); };
  var HEIGHT_KEY = 'eb-fit-height';

  /* THE ENDPOINT, NORMALISED ONCE. /apps/reviews/ and /apps/reviews are not the
     same request to an app proxy, and a merchant who types the slash has not
     made a mistake worth a broken wall. Liquid takes it off where the setting
     is read; this takes it off again for a data-endpoint written by hand or by
     an app. One rule, both sides of the wire. */
  function clean(url) {
    return String(url || '').trim().replace(/\/+$/, '');
  }

  function t(box, key, fallback) {
    var v = box.getAttribute('data-t-' + key);
    return v || fallback;
  }

  /* The height the fit finder was left at, if this visitor ever moved it.
     Nothing is stored unless they did — an untouched slider is not an
     opinion about anybody's height. */
  function knownHeight() {
    try { return parseInt(localStorage.getItem(HEIGHT_KEY), 10) || 0; } catch (e) { return 0; }
  }

  function bandFor(h, bands) {
    for (var i = 0; i < bands.length; i++) {
      var m = bands[i].match(/(\d+)\D+(\d+)/);
      if (m && h >= +m[1] && h <= +m[2]) return bands[i];
    }
    return '';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function stars(score) {
    var pct = Math.max(0, Math.min(5, +score || 0)) / 5 * 100;
    var row = '';
    for (var i = 0; i < 5; i++) row += '<svg class="eb-icon eb-icon--xs" aria-hidden="true"><use href="#i-star"/></svg>';
    return '<span class="eb-stars" style="--pct:' + pct + '%" role="img" aria-label="' +
      (Math.round(score * 10) / 10) + '">' +
      '<span class="eb-stars__off" aria-hidden="true">' + row + '</span>' +
      '<span class="eb-stars__on" aria-hidden="true">' + row + '</span></span>';
  }

  /* The card, in JS, drawn to the same shape snippets/review-card.liquid
     prints. Two copies of one card is how a wall ends up disagreeing with
     itself, so this is the only place the JS card is written down. */
  function card(rv, match) {
    var media = '';
    (rv.media || []).slice(0, 4).forEach(function (m) {
      media += '<button type="button"' + (m.type === 'video' ? ' class="is-film"' : '') +
        ' data-eb-review-media="' + esc(m.url) + '"><img src="' + esc(m.poster || m.url) +
        '" alt="" loading="lazy">' +
        (m.type === 'video' ? '<svg class="eb-icon eb-icon--sm" aria-hidden="true"><use href="#i-play"/></svg>' : '') +
        '</button>';
    });
    var r = rv.rider || {};
    var bits = [];
    if (r.height_band) bits.push('<b>' + esc(r.height_band) + '</b>');
    if (r.weight_band) bits.push('<b>' + esc(r.weight_band) + '</b>');
    if (r.age_band) bits.push('<b>' + esc(r.age_band) + '</b>');
    if (r.gender) bits.push('<b>' + esc(r.gender) + '</b>');
    /* AN ACCESSORY REVIEW'S ONE DIMENSION. Height and weight decide whether
       somebody's opinion of a BIKE is worth anything to you and say nothing
       whatever about a rack; which bike they bolted it to says everything.
       Absent on a bike review, so the same card prints both kinds. */
    if (r.fitted_to) bits.push('<b>' + esc(r.fitted_to) + '</b>');
    if (r.fitted_how) bits.push(esc(r.fitted_how));
    if (r.variant_title) bits.push('<b>' + esc(r.variant_title) + '</b>');
    if (r.use) bits.push(esc(r.use));
    return '<article class="eb-rvcard' + (match ? ' is-match' : '') + '">' +
      '<div class="eb-rvcard__head"><span class="eb-rvcard__who">' + esc(rv.author) + '</span>' +
      (rv.verified ? '<span class="eb-rvcard__v"><svg class="eb-icon eb-icon--xs" aria-hidden="true">' +
        '<use href="#i-badge-check"/></svg>' + esc(rv.verified_label || 'Verified') + '</span>' : '') +
      (rv.created_at ? '<span class="eb-rvcard__when">' + esc(rv.created_at) + '</span>' : '') +
      '</div>' + stars(rv.rating) +
      (rv.title ? '<p class="eb-rvcard__title">' + esc(rv.title) + '</p>' : '') +
      (rv.body ? '<p class="eb-rvcard__body">' + esc(rv.body) + '</p>' : '') +
      (media ? '<div class="eb-rvcard__media">' + media + '</div>' : '') +
      /* THE SHOP'S ANSWER, UNDER THE REVIEW IT ANSWERS. A one-star review with
         a reply is a different thing from a one-star review without one: the
         complaint is the same, but one shop turned up and the other did not,
         and the reader is deciding about the shop as much as the product.
         It is printed after the body and before the rider line — the reply
         belongs to what was said, not to who said it.
         The app sends `reply` and `reply_by` on every card; before this they
         arrived and were dropped on the floor.
         The look is inline and written in the theme's own tokens, so this is
         ONE file to replace and nothing to paste into a stylesheet. Give
         `.eb-rvcard__reply` a rule in sections.css and it wins over these. */
      (rv.reply ? '<div class="eb-rvcard__reply" style="display:grid;gap:4px;' +
        'border-inline-start:2px solid var(--color-border);padding-inline-start:var(--space-4)">' +
        (rv.reply_by ? '<b style="font:var(--type-micro);color:var(--color-text-muted)">' +
          esc(rv.reply_by) + '</b>' : '') +
        '<p style="font:var(--type-body-sm);color:var(--color-text-2)">' + esc(rv.reply) +
        '</p></div>' : '') +
      (bits.length ? '<div class="eb-rvcard__rider"><svg class="eb-icon eb-icon--xs" aria-hidden="true">' +
        '<use href="#i-rider"/></svg>' + bits.join('<i>·</i>') + '</div>' : '') +
      (rv.product_title ? '<div class="eb-rvcard__foot"><span class="eb-rvcard__for">' +
        '<svg class="eb-icon eb-icon--xs" aria-hidden="true"><use href="#i-bike"/></svg>' +
        esc(rv.product_title) + '</span></div>' : '') +
      '</article>';
  }

  function wall(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    var grid = box.querySelector('[data-eb-reviews-grid]');
    if (!endpoint || !grid) return;

    var per = parseInt(box.getAttribute('data-per'), 10) || 12;
    var product = box.getAttribute('data-product');
    var matchBox = box.querySelector('[data-eb-reviews-match]');
    var countEl = box.querySelector('[data-eb-reviews-count]');
    var foot = box.querySelector('[data-eb-reviews-foot]');
    var moreBtn = box.querySelector('[data-eb-reviews-more]');
    var clearEl = box.querySelector('[data-eb-reviews-clear]');
    var bar = box.querySelector('[data-eb-reviews-bar]');
    var filters = { height: '', use: '', config: '', kind: '' };
    /* THE WALL OPENS ON "SHOW ALL". The match is offered, not applied: the
       banner says how many would match this visitor and "Match them to me"
       narrows it in one press. The first request is exactly the one the Show
       all button sends. */
    var matching = false, page = 1, pages = 1, busy = false;

    /* WHAT THE PAGE ALREADY KNOWS. The variant comes from the picker and is
       live — change the size and the wall follows. The height comes from the
       fit finder, and only if it was moved. */
    var seed = { config: '', height: '' };
    function readVariant() {
      if (box.getAttribute('data-match-variant') !== '1') return '';
      /* The theme's own hidden field first — main-product keeps it correct and
         it is the field the cart will actually post. A selector LIST would not
         do here: it returns the first element in DOCUMENT order that matches
         any of its selectors, so `[name=id]:checked, [name=id]` hands back the
         first radio on the page rather than the checked one. Measured: the
         wall never noticed the size being changed. */
      var el = document.querySelector('[data-eb-variant-id]') ||
               document.querySelector('form[action*="/cart/add"] [name="id"]:checked') ||
               document.querySelector('form[action*="/cart/add"] [name="id"]');
      return el ? el.value : '';
    }
    function readHeight() {
      if (box.getAttribute('data-match-height') !== '1') return '';
      var h = knownHeight();
      if (!h) return '';
      var bands = slice(bar.querySelectorAll('[data-dim="height"] [data-val]'))
        .map(function (b) { return b.textContent.trim(); })
        .filter(function (v) { return /\d/.test(v); });
      return bandFor(h, bands);
    }

    function labelFor(dim, val) {
      var row = bar.querySelector('[data-dim="' + dim + '"] [data-val="' + CSS.escape(val) + '"]');
      return row ? row.childNodes[0].textContent.trim() : val;
    }

    function paintBar() {
      slice(bar.querySelectorAll('[data-dim]')).forEach(function (d) {
        var dim = d.getAttribute('data-dim');
        var cur = d.querySelector('[data-eb-reviews-current]');
        var val = filters[dim];
        /* WHAT IS APPLIED IS WHAT IS SHOWN. While the matching is on, the
           height and the configuration ARE set — by the page rather than by
           the visitor — so the buttons say so. A button reading "Any" over a
           wall that has been narrowed is the same lie the banner exists to
           prevent, told twice. */
        var seeded = matching ? (dim === 'height' ? seed.height : dim === 'config' ? seed.config : '') : '';
        var show = val || seeded;
        if (cur) cur.textContent = show ? labelFor(dim, show) : t(box, 'any', cur.textContent);
        slice(d.querySelectorAll('[data-val]')).forEach(function (b) {
          b.setAttribute('aria-selected', b.getAttribute('data-val') === show ? 'true' : 'false');
        });
      });
      var any = Object.keys(filters).some(function (k) { return filters[k]; });
      if (clearEl) clearEl.hidden = !any;
    }

    function paintMatch(total, matched) {
      if (!matchBox) return;
      var applied = [];
      if (seed.height) applied.push(seed.height);
      if (seed.config) applied.push(labelFor('config', seed.config));
      if (!applied.length) { matchBox.hidden = true; return; }
      matchBox.hidden = false;
      matchBox.classList.toggle('eb-rvmatch--off', !matching);
      var title = matchBox.querySelector('[data-eb-reviews-match-title]');
      var why = matchBox.querySelector('[data-eb-reviews-match-why]');
      var n = matchBox.querySelector('[data-eb-reviews-match-n]');
      var btn = matchBox.querySelector('[data-eb-reviews-toggle]');
      if (matching) {
        title.textContent = t(box, 'matched', 'Matched to you') + ': ' + applied.join(' · ');
        why.textContent = t(box, 'why', '');
        if (n) n.textContent = (matched != null && total != null) ? matched + ' / ' + total : '';
        if (btn) btn.textContent = t(box, 'showall', 'Show all') + (total != null ? ' ' + total : '');
      } else {
        title.textContent = t(box, 'showingall', 'Showing all reviews');
        why.textContent = matched != null ? (matched + ' ' + t(box, 'wouldmatch', 'match you')) : '';
        if (n) n.textContent = '';
        if (btn) btn.textContent = t(box, 'matchme', 'Match them to me');
      }
    }

    function query(p) {
      var q = ['page=' + p, 'per=' + per];
      if (product) q.push('product=' + encodeURIComponent(product));
      var h = filters.height || (matching ? seed.height : '');
      var c = filters.config || (matching ? seed.config : '');
      if (h) q.push('height=' + encodeURIComponent(h));
      if (c) q.push('variant=' + encodeURIComponent(c));
      if (filters.use) q.push('use=' + encodeURIComponent(filters.use));
      if (filters.kind) q.push('kind=' + encodeURIComponent(filters.kind));
      return endpoint + (endpoint.indexOf('?') > -1 ? '&' : '?') + q.join('&');
    }

    function facets(f) {
      if (!f) return;
      slice(bar.querySelectorAll('[data-dim]')).forEach(function (d) {
        var dim = d.getAttribute('data-dim');
        var set = f[dim === 'config' ? 'variant' : dim];
        if (!set) return;
        slice(d.querySelectorAll('[data-val]')).forEach(function (b) {
          var meta = b.querySelector('.eb-menu__meta');
          var v = b.getAttribute('data-val');
          if (meta) meta.textContent = v && set[v] != null ? set[v] : '';
        });
      });
    }

    /* NO CARD IS AS TALL AS ITS NEIGHBOUR. A grid row is as tall as its
       tallest card, so one long review with a reply left a screen of empty
       card beside it — on a desktop as boxes stretched to match, on a phone as
       a rail with nothing under the short slides. So the cards are laid out by
       height, in two shapes:

       DESKTOP · COLUMNS. As many columns as the old grid had (280px minimum),
       and each card goes to the shortest one, in order. Newest reviews still
       read across the top. On "Show more" the new cards are added to the
       columns already there, so nothing already read moves.

       PHONE · SLIDES. The rail from sections.css, but each slide is filled
       with cards top to bottom until the next would make it taller than the
       tallest single card (or about half a screen when every card is short).
       A long review stands alone; short ones share. On append only the last
       slide may take more.

       Every card carries its arrival order (data-i), so switching between the
       two, or to a different column count, rebuilds from the original order. */
    var phone = window.matchMedia('(max-width: 600px)');
    var SLIDE_GAP = 12, COL_MIN = 280;
    var seq = 0, shape = '';

    function cards() {
      return slice(grid.querySelectorAll('.eb-rvcard')).sort(function (a, b) {
        return (+a.getAttribute('data-i')) - (+b.getAttribute('data-i'));
      });
    }

    function unpack() {
      var all = cards();
      slice(grid.querySelectorAll('.eb-rvslide,.eb-rvcol')).forEach(function (w) { w.remove(); });
      all.forEach(function (c) { grid.appendChild(c); });
      grid.classList.remove('is-cols');
      grid.style.removeProperty('--cols');
      shape = '';
    }

    function colCount() {
      var gap = parseFloat(getComputedStyle(grid).columnGap) || 16;
      return Math.max(1, Math.floor((grid.clientWidth + gap) / (COL_MIN + gap)));
    }

    function packSlides(loose, append) {
      var hs = loose.map(function (c) { return c.getBoundingClientRect().height; });
      var old = append ? slice(grid.querySelectorAll('.eb-rvslide')) : [];
      var tallest = Math.max.apply(null, hs.concat(old.map(function (sl) { return sl.offsetHeight; })));
      var budget = Math.max(tallest, Math.min(window.innerHeight * 0.55, 480));
      var open = old.length ? [{ el: old[old.length - 1], h: old[old.length - 1].offsetHeight }] : [];
      loose.forEach(function (card, i) {
        var fit = null;
        for (var k = 0; k < open.length; k++) {
          if (open[k].h + SLIDE_GAP + hs[i] <= budget) { fit = open[k]; break; }
        }
        if (!fit) {
          var el = document.createElement('div');
          el.className = 'eb-rvslide';
          fit = { el: el, h: -SLIDE_GAP };
          open.push(fit);
        }
        fit.el.appendChild(card);
        fit.h += SLIDE_GAP + hs[i];
        if (!fit.el.parentNode) grid.appendChild(fit.el);
      });
    }

    function packCols(loose, n) {
      var cols = slice(grid.querySelectorAll('.eb-rvcol'));
      if (!cols.length) {
        grid.classList.add('is-cols');
        grid.style.setProperty('--cols', n);
        for (var k = 0; k < n; k++) {
          var el = document.createElement('div');
          el.className = 'eb-rvcol';
          grid.appendChild(el);
          cols.push(el);
        }
      }
      // Measured after every card: the column width is fixed now, so the
      // height a card adds is the height it really has.
      loose.forEach(function (card) {
        var low = cols[0];
        cols.forEach(function (c) { if (c.offsetHeight < low.offsetHeight) low = c; });
        low.appendChild(card);
      });
    }

    function pack(append) {
      slice(grid.children).forEach(function (el) {
        if (el.classList.contains('eb-rvcard') && !el.hasAttribute('data-i')) el.setAttribute('data-i', seq++);
      });
      var want = phone.matches ? 'slides' : 'cols:' + colCount();
      if (!append || want !== shape) unpack();
      var loose = slice(grid.children).filter(function (el) { return el.classList.contains('eb-rvcard'); });
      shape = want;
      if (!loose.length) return;
      if (phone.matches) packSlides(loose, append && grid.querySelector('.eb-rvslide'));
      else packCols(loose, +want.split(':')[1]);
    }

    function rebuild() { if (grid.querySelector('.eb-rvcard')) pack(false); }
    if (phone.addEventListener) phone.addEventListener('change', rebuild);
    else if (phone.addListener) phone.addListener(rebuild);
    var resizeT;
    window.addEventListener('resize', function () {
      clearTimeout(resizeT);
      resizeT = setTimeout(function () {
        if (!phone.matches && shape !== 'cols:' + colCount()) rebuild();
      }, 150);
    });

    function load(p, append) {
      if (busy) return;
      busy = true;
      box.classList.add('is-loading');
      fetch(query(p), { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function (data) {
          page = data.page || p;
          pages = data.pages || 1;
          var rows = (data.reviews || []).map(function (rv) {
            return card(rv, matching && !!(seed.height || seed.config));
          }).join('');
          if (append) grid.insertAdjacentHTML('beforeend', rows);
          else { seq = 0; shape = ''; grid.innerHTML = rows; grid.scrollLeft = 0; }
          pack(append);
          facets(data.facets);
          paintMatch(data.total, data.matched);
          paintBar();
          if (countEl) {
            countEl.textContent = (data.matched != null && data.total != null && data.matched !== data.total)
              ? data.matched + ' / ' + data.total : (data.total != null ? String(data.total) : '');
          }
          if (foot) foot.hidden = false;
          if (moreBtn) {
            moreBtn.hidden = page >= pages;
            moreBtn.textContent = t(box, 'more', 'Show more');
          }
          box.classList.toggle('is-empty', !(data.reviews || []).length);
        })
        .catch(function () {
          /* The app is not answering. Say nothing rather than draw an empty
             wall that looks like a bike nobody has reviewed. */
          box.classList.add('is-off');
          if (foot) foot.hidden = true;
        })
        .then(function () { busy = false; box.classList.remove('is-loading'); });
    }

    function reset() { page = 1; load(1, false); }

    /* the controls */
    bar.addEventListener('click', function (e) {
      var row = e.target.closest('[data-val]');
      if (row && bar.contains(row)) {
        var dim = row.closest('[data-dim]').getAttribute('data-dim');
        filters[dim] = row.getAttribute('data-val');
        /* Touching a dimension by hand ends the automatic matching on it —
           the visitor has said what they want and the page should not argue. */
        if (dim === 'height' || dim === 'config') matching = false;
        var reg = row.closest('[data-eb-region]');
        if (reg) reg.removeAttribute('open');
        paintBar(); reset();
        return;
      }
      var clear = e.target.closest('[data-eb-reviews-clear]');
      if (clear) {
        e.preventDefault();
        Object.keys(filters).forEach(function (k) { filters[k] = ''; });
        matching = false;
        paintBar(); reset();
      }
    });

    if (matchBox) matchBox.addEventListener('click', function (e) {
      if (!e.target.closest('[data-eb-reviews-toggle]')) return;
      matching = !matching;
      reset();
    });

    if (moreBtn) moreBtn.addEventListener('click', function () { load(page + 1, true); });

    /* ON A PHONE THE WALL IS A RAIL (sections.css), and a "Show more" button
       under a sideways row is a button nobody scrolls down to find. So the next
       page is fetched when the reader is within a card of the end. On a desktop
       the grid does not scroll sideways, scrollWidth equals clientWidth, and
       this never fires. `busy` in load() stops it firing twice. */
    grid.addEventListener('scroll', function () {
      if (!moreBtn || moreBtn.hidden || busy) return;
      if (grid.scrollWidth <= grid.clientWidth + 1) return;
      if (grid.scrollLeft + grid.clientWidth * 2 >= grid.scrollWidth) load(page + 1, true);
    }, { passive: true });

    /* THE PICKER MOVES, THE WALL FOLLOWS. This is the whole reason the theme
       does the matching and not the app: the app cannot see either of these.
       eb:variant is the theme's own event, raised by the product controller
       whenever a size or a colour is chosen; the change listener is for a
       picker built out of plain radios. */
    function followVariant() {
      var v = readVariant();
      if (v && v !== seed.config) { seed.config = v; if (matching) reset(); }
    }
    document.addEventListener('eb:variant', followVariant);
    document.addEventListener('change', function (e) {
      if (e.target.closest('form[action*="/cart/add"]')) followVariant();
    });

    seed.config = readVariant();
    seed.height = readHeight();
    paintBar();
    load(1, false);
  }

  /* ══ THE ACCOUNT'S FORM ════════════════════════════════════════════════
     Opening it is the only thing the theme does that the app cannot: the
     button knows which product, which variant and which ORDER it came from,
     because the theme printed the row from the customer's own orders. That is
     what makes the review verified, and it is why the form lives here.

     The height is prefilled from the fit finder if this browser has one — the
     visitor can change it, and it is sent as a figure and stored as a band.

     THE THEME DOES NOT UPLOAD ANYTHING. The files ride the same POST as the
     words; the thumbnails below the dropzone are drawn from the local File
     objects and nothing leaves the browser until submit. */
  function accountForm(box) {
    var form = box.querySelector('[data-eb-review-form]');
    var panel = box.querySelector('[data-eb-account-form]');
    if (!form || !panel) return;

    var thumb = form.querySelector('[data-eb-review-thumb]');
    var title = form.querySelector('[data-eb-review-title]');
    var sub = form.querySelector('[data-eb-review-sub]');
    var files = form.querySelector('[data-eb-review-files]');
    var strip = form.querySelector('[data-eb-review-uploads]');

    function open(btn) {
      panel.hidden = false;
      ['product', 'variant', 'order'].forEach(function (k) {
        var f = form.querySelector('[data-eb-review-' + k + ']');
        if (f) f.value = btn.getAttribute('data-' + k) || '';
      });
      var vt = btn.getAttribute('data-variant-title') || '';
      /* Shopify's name for "this product has no variants". Printing it as a
         configuration would be printing a database default at a customer. */
      if (vt === 'Default Title') vt = '';
      if (title) title.textContent = btn.getAttribute('data-title') || '';
      if (sub) sub.textContent = vt;
      /* The frame and battery cell in the rider block is the same fact, and it
         is hidden until there is one — an accessory has none. */
      var fixed = form.querySelector('[data-eb-review-fixed]');
      var fixedField = form.querySelector('[data-eb-review-fixed-field]');
      if (fixed) fixed.textContent = vt;
      if (fixedField) fixedField.hidden = !vt;
      if (thumb) {
        var src = btn.getAttribute('data-image') || '';
        thumb.src = src;
        thumb.hidden = !src;
      }
      /* THE HEIGHT IS NOT PREFILLED ANY MORE. The fit finder's slider was moved
         to look at bikes, by whoever was holding the phone, and that is not an
         answer about the writer's height — it is a guess printed on somebody
         else's review. It is typed now, with the weight, the age and the
         answer to who is riding it. knownHeight() still opens the WALL on the
         right band, which is a different question. */
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
      var first = form.querySelector('.eb-ratepick input');
      if (first) first.focus();
    }

    box.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-eb-account-write]');
      if (btn) { e.preventDefault(); open(btn); return; }
      if (e.target.closest('[data-eb-account-cancel]')) { e.preventDefault(); panel.hidden = true; }
    });

    /* The word beside the stars is handled once, for the whole document, by
       ratepickWord() — the account form is no longer the only place a rating
       is picked. */

    if (files && strip) files.addEventListener('change', function () {
      strip.textContent = '';
      slice(files.files).forEach(function (f, i) {
        var item = document.createElement('span');
        item.className = 'eb-uploads__item';
        if (f.type.indexOf('video') === 0) {
          item.style.cssText = 'display:grid;place-items:center;background:var(--color-inverse);color:var(--color-on-inverse)';
          item.innerHTML = '<svg class="eb-icon eb-icon--sm" aria-hidden="true"><use href="#i-play"/></svg>';
        } else {
          var img = document.createElement('img');
          img.src = URL.createObjectURL(f);
          img.alt = '';
          img.onload = function () { URL.revokeObjectURL(img.src); };
          item.appendChild(img);
        }
        strip.appendChild(item);
      });
    });

    /* THE HANDSHAKE. The app can take the submission by cancelling this
       event; if nobody does, the form posts itself the ordinary way. */
    form.addEventListener('submit', function (e) {
      var ev = new CustomEvent('eb:review:submit', {
        bubbles: true, cancelable: true,
        detail: { form: form, payload: new FormData(form) }
      });
      var proceed = form.dispatchEvent(ev);
      if (!proceed) { e.preventDefault(); form.classList.add('is-sending'); }
    });
  }

  /* ══ THE SUMMARY, THE SUBJECTS AND THE RECORD ══════════════════════════
     Three bands that print numbers the app owns: the score with its
     distribution, the counted subjects, and what changed. Each one is typed
     into the theme so the page has something true to print with no script at
     all — and each one is REPLACED by the app's answer when an endpoint is
     given, so a figure that has moved corrects itself instead of sitting
     there being wrong.

     Nothing is invented here. If the app does not answer, what the shop typed
     stands; if it answers with a field missing, that field is left alone. */
  function fetchJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); });
  }

  function summary(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    if (!endpoint) return;
    fetchJSON(endpoint + '/summary').then(function (d) {
      var scoreEl = box.querySelector('[data-eb-summary-score]');
      if (scoreEl && d.score != null) {
        scoreEl.firstChild.textContent = Math.round(d.score * 10) / 10;
      }
      var starsEl = box.querySelector('.eb-stars');
      if (starsEl && d.score != null) {
        starsEl.style.setProperty('--pct', (Math.max(0, Math.min(5, d.score)) / 5 * 100) + '%');
      }
      var of = box.querySelector('[data-eb-summary-of]');
      if (of && d.note) of.textContent = d.note;
      var dist = box.querySelector('[data-eb-summary-dist]');
      if (dist && d.distribution) {
        var top = 0;
        Object.keys(d.distribution).forEach(function (k) {
          if (d.distribution[k] > top) top = d.distribution[k];
        });
        slice(dist.querySelectorAll('.eb-dist__row')).forEach(function (row) {
          var k = row.querySelector('.eb-dist__k').textContent.trim();
          var n = d.distribution[k];
          if (n == null) return;
          row.querySelector('.eb-dist__n').textContent = n;
          row.querySelector('.eb-dist__bar i').style.setProperty(
            '--val', (top ? Math.round(n * 100 / top) : 0) + '%');
        });
      }
    }).catch(function () {});
  }

  function topics(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    if (!endpoint) return;
    fetchJSON(endpoint + '/topics').then(function (d) {
      var rows = d.topics || [];
      if (!rows.length) return;
      var top = rows.reduce(function (m, r) { return Math.max(m, r.count || 0); }, 0);
      /* The shop's blocks decide WHICH subjects and in what order — six of
         them, fixed, so the list cannot grow into a word cloud. The app only
         decides how many people said each one. */
      slice(box.querySelectorAll('.eb-topic')).forEach(function (el) {
        var key = el.getAttribute('data-eb-topic');
        var row = rows.filter(function (r) { return r.value === key; })[0];
        if (!row || row.count == null) return;
        el.querySelector('.eb-topic__n').textContent = row.count;
        el.querySelector('.eb-topic__bar i').style.setProperty(
          '--val', (top ? Math.round(row.count * 100 / top) : 0) + '%');
      });
    }).catch(function () {});
  }

  function changes(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    if (!endpoint) return;
    fetchJSON(endpoint + '/changes').then(function (d) {
      var rows = d.changes || [];
      if (!rows.length) return;
      /* The app owns the quotes — they are real reviews, and the band picks a
         different one each time it turns. It can own the whole row as well:
         send `did` and `note` and the shop does not have to type them twice. */
      slice(box.querySelectorAll('[data-eb-said-row]')).forEach(function (el, i) {
        var row = rows.filter(function (r) { return r.id && r.id === el.getAttribute('data-id'); })[0]
                  || rows[i];
        if (!row) return;
        var q = el.querySelector('[data-eb-said-quote]');
        if (q && row.quotes && row.quotes.length) {
          q.setAttribute('data-quotes', JSON.stringify(row.quotes));
          q.textContent = row.quotes[Math.floor(Math.random() * row.quotes.length)];
        }
        var did = el.querySelector('.eb-said__did b');
        if (did && row.did) did.textContent = row.did;
        var note = el.querySelector('.eb-said__did span');
        if (note && row.note) note.textContent = row.note;
        var meta = el.querySelector('.eb-said__did em');
        if (meta && row.meta) meta.textContent = row.meta;
        var when = el.querySelector('.eb-said__when');
        if (when && row.when) when.textContent = row.when;
      });
    }).catch(function () {});
  }

  /* ══ THE WORD BESIDE THE STARS ═════════════════════════════════════════
     One listener for the whole document. Five identical stars are a shape;
     the word is what makes a five-point scale mean the same thing to two
     people, and it is now needed in three places — the account form, the
     invitation's receipt and the invitation's form — so it stopped being the
     account form's business. The word is the pressed label's title, and it
     sits beside the fieldset, so it is found from the fieldset's parent. */
  function ratepickWord(input) {
    var pick = input.closest('.eb-ratepick');
    if (!pick || !pick.parentNode) return;
    var word = pick.parentNode.querySelector('[data-eb-ratepick-word]');
    var label = pick.querySelector('label[for="' + input.id + '"]');
    if (word && label) word.textContent = label.getAttribute('title') || '';
  }

  document.addEventListener('change', function (e) {
    var r = e.target.closest && e.target.closest('.eb-ratepick input');
    if (r) ratepickWord(r);
  });

  /* ══ THE INVITATION ════════════════════════════════════════════════════
     The page a review letter lands on. It does four things and refuses to do
     a fifth:

       1 · THE STAR ARRIVES ALREADY PRESSED. ?stars=4 in the link is the press
           made in the letter. It is shown back rather than asked for again,
           and the note under it changes to say where it came from.
       2 · THERE IS ONE RATING ON THE PAGE and it is drawn twice — the receipt
           and the form. Pressing either moves both, or the page posts a number
           the reader did not choose.
       3 · THE CARD IS FILLED FROM THE ORDER, through the app, because the
           visitor is usually not signed in. A cell the answer does not carry
           STAYS HIDDEN and the row's data-n is recounted, so two facts lay out
           as two rather than as three with a hole. Nothing becomes an em dash.
       4 · THE SECOND CLICK. If the answer says this order already has a
           review, the ask is spent: the receipt takes its place and the form
           goes away, rather than asking a person to write what they wrote.

     What it refuses: inventing anything. No endpoint, no answer, or an answer
     with a field missing, and what Liquid printed stands. */
  function param(name) {
    var m = new RegExp('[?&]' + name + '=([^&#]*)').exec(location.search);
    return m ? decodeURIComponent(m[1].replace(/\+/g, ' ')) : '';
  }

  function invite(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    var token = param('token') || param('o');
    var stars = parseInt(param('stars'), 10);
    var picks = slice(document.querySelectorAll('.eb-ratepick'));

    var tokenField = document.querySelector('[data-eb-invite-token]');
    if (tokenField) tokenField.value = token;

    /* ── one rating, two pickers ─────────────────────────────────────── */
    function setStar(v) {
      picks.forEach(function (pick) {
        var input = pick.querySelector('input[value="' + v + '"]');
        if (!input) return;
        input.checked = true;
        ratepickWord(input);
      });
    }

    document.addEventListener('change', function (e) {
      var r = e.target.closest && e.target.closest('.eb-ratepick input');
      if (!r || r.ebSyncing) return;
      picks.forEach(function (pick) {
        if (pick.contains(r)) return;
        var other = pick.querySelector('input[value="' + r.value + '"]');
        if (!other || other.checked) return;
        other.ebSyncing = true;
        other.checked = true;
        ratepickWord(other);
        other.ebSyncing = false;
      });
    });

    if (stars >= 1 && stars <= 5) {
      setStar(stars);
      var note = box.querySelector('[data-eb-invite-carried]');
      var carried = note && note.getAttribute('data-carried');
      if (note && carried) note.textContent = carried;

      /* ── THE UNHAPPY ROUTE COMES FIRST, and only its ORDER changes ────
         A shop that answers "my brake rubs" with "please rate us" has earned
         the two stars. So on one or two the repair routes are moved above the
         form. Nothing is hidden and nothing is reworded: the form is the same
         form, in the same page, one screen further down. */
      if (stars <= 2) {
        var router = document.querySelector('.eb-router-section');
        var form = document.querySelector('.eb-inviteform-section');
        if (router && form && form.parentNode && router.compareDocumentPosition(form)
            & Node.DOCUMENT_POSITION_PRECEDING) {
          form.parentNode.insertBefore(router, form);
        }
      }
    }

    if (!endpoint || !token) return;

    fetchJSON(endpoint + '/invite?token=' + encodeURIComponent(token)).then(function (d) {
      var facts = box.querySelector('[data-eb-invite-facts]');
      var order = d.order || {};
      var product = d.product || {};

      function cell(key, value, label) {
        if (!facts || !value) return;
        var el = facts.querySelector('[data-eb-invite-cell="' + key + '"]');
        if (!el) return;
        el.querySelector('[data-eb-invite-value]').textContent = value;
        var lab = el.querySelector('[data-eb-invite-label]');
        if (lab && label) lab.textContent = label;
        el.hidden = false;
      }

      cell('id', order.frame || order.name,
           order.frame ? facts && facts.getAttribute('data-label-frame')
                       : facts && facts.getAttribute('data-label-order'));
      cell('date', order.delivered || order.ordered,
           order.delivered ? facts && facts.getAttribute('data-label-delivered')
                           : facts && facts.getAttribute('data-label-ordered'));
      cell('variant', product.variant_title, facts && facts.getAttribute('data-label-variant'));

      /* THE COUNT IS RECOUNTED FROM WHAT IS ACTUALLY SHOWING, never from what
         was asked for. It is the stylesheet's only input for this row. */
      if (facts) {
        var shown = slice(facts.querySelectorAll('[data-eb-invite-cell]')).filter(
          function (el) { return !el.hidden; }).length;
        facts.setAttribute('data-n', shown);
        facts.hidden = shown === 0;
      }

      var titleEl = box.querySelector('[data-eb-invite-title]');
      if (titleEl && product.title) titleEl.textContent = product.title;

      var media = box.querySelector('[data-eb-invite-media]');
      var img = media && media.querySelector('img');
      if (media && img && product.image && !img.getAttribute('src')) {
        img.src = product.image;
        media.hidden = false;
      }

      /* the form: the hidden ids, the fixed variant, and the product row */
      [['[data-eb-review-product]', product.id],
       ['[data-eb-review-variant]', product.variant_id],
       ['[data-eb-review-order]', order.id]].forEach(function (row) {
        var f = document.querySelector(row[0]);
        if (f && row[1] != null) f.value = row[1];
      });
      var fixed = document.querySelector('[data-eb-review-fixed]');
      if (fixed && product.variant_title) {
        fixed.textContent = product.variant_title;
        var fixedField = fixed.closest('[data-eb-review-fixed-field]');
        if (fixedField) fixedField.hidden = false;
      }

      var prow = document.querySelector('[data-eb-invite-product]');
      if (prow && (product.title || product.image)) {
        var pt = prow.querySelector('[data-eb-review-title]');
        var ps = prow.querySelector('[data-eb-review-sub]');
        var pi = prow.querySelector('[data-eb-review-thumb]');
        if (pt && product.title) pt.textContent = product.title;
        if (ps) ps.textContent = [product.variant_title, order.verified_note].filter(
          function (x) { return x; }).join(' · ');
        if (pi && product.image) { pi.src = product.image; pi.hidden = false; }
        prow.hidden = false;
      }

      /* ── the second click ─────────────────────────────────────────── */
      if (d.review) {
        var written = box.querySelector('[data-eb-invite-written]');
        if (written) written.hidden = false;
        var starbox = box.querySelector('.eb-invite__stars');
        if (starbox) starbox.hidden = true;
        var cta = box.querySelector('.eb-btn-row');
        if (cta) cta.hidden = true;
        var formSection = document.querySelector('.eb-inviteform-section');
        if (formSection) formSection.hidden = true;
      }
    }).catch(function () {});
  }

  /* ══ THE ACCESSORY WALL ════════════════════════════════════════════════
     Three cards and a sentence, matched to the bike chosen in the fit gate at
     the top of the page. It is the bike wall's rule with the bike wall's own
     card, on the one dimension an accessory has: the app is asked
     `fitted_to=<product id>` exactly the way it is asked `variant=` upstairs.

     BOTH NUMBERS, ALWAYS. "9 of 41" — a band quietly showing nine reviews
     reads as a rack nine people bought, which is worse than not matching.
     Nothing to show and the grid stays empty: an accessory with no reviews yet
     looks exactly like one whose reviews have not arrived, and only one of
     those is worth drawing. */
  function accWall(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    var grid = box.querySelector('[data-eb-acc-grid]');
    if (!endpoint || !grid) return;
    var per = parseInt(box.getAttribute('data-per'), 10) || 3;
    var product = box.getAttribute('data-product') || '';
    var total = parseInt(box.getAttribute('data-total'), 10) || 0;
    var titleEl = box.querySelector('[data-eb-acc-match-t]');
    var whyEl = box.querySelector('[data-eb-acc-match-why]');
    var nEl = box.querySelector('[data-eb-acc-match-n]');
    var clear = box.querySelector('[data-eb-acc-clear]');
    var current = '';

    function fill(rows, matched, bikeName) {
      grid.textContent = '';
      rows.slice(0, per).forEach(function (rv, i) {
        /* THE FOLD TAKES ONE PLAIN CHILD — padding does not collapse, so the
           card sits one level deeper and the wrapper is what opens. */
        var outer = document.createElement('div');
        outer.setAttribute('data-reveal', 'fold');
        outer.style.setProperty('--i', i);
        var inner = document.createElement('div');
        inner.innerHTML = card(rv, !!bikeName && String((rv.rider || {}).fitted_to_id || '') === current);
        outer.appendChild(inner);
        grid.appendChild(outer);
        /* Already in view when the answer lands, so they are opened here rather
           than waiting for an observer that has finished its pass. */
        requestAnimationFrame(function () { outer.classList.add('is-revealed'); });
      });

      var t = box.getAttribute('data-t-all') || '';
      var why = box.getAttribute('data-t-why') || '';
      var n = total;
      if (bikeName) {
        n = matched == null ? 0 : matched;
        t = (n ? (box.getAttribute('data-t-matched') || '') : (box.getAttribute('data-t-none') || ''));
        t = t.replace('[matched]', n).replace('[bike]', bikeName);
        why = '';
      }
      t = t.replace('[total]', total);
      if (titleEl) titleEl.textContent = t;
      if (whyEl) whyEl.textContent = why;
      if (nEl) nEl.textContent = bikeName ? (n + ' / ' + total) : total;
      if (clear) clear.hidden = !bikeName;
    }

    function load(bike, bikeName) {
      current = bike || '';
      var q = endpoint + '?product=' + encodeURIComponent(product) + '&per=' + per
            + (bike ? '&fitted_to=' + encodeURIComponent(bike) : '');
      fetchJSON(q).then(function (d) {
        fill(d.reviews || [], d.matched, bikeName);
      }).catch(function () {});
    }

    load('', '');

    /* The gate is the page's one question; this band answers it again in its
       own words. eb-product.js raises the event; nothing else couples them. */
    document.addEventListener('eb:fit:pick', function (e) {
      var v = (e.detail || {}).value || '';
      if (!v || v === 'none' || v === 'other') { load('', ''); return; }
      var opt = document.querySelector('[data-eb-fitgate-pick] option[value="' + v + '"]');
      load(v, opt ? opt.textContent.trim() : '');
    });

    if (clear) clear.addEventListener('click', function () { load('', ''); });
  }

  /* ══ BLACK FRIDAY · OWNERS ═════════════════════════════════════════════
     黑五页的「车主怎么说」：每个产品问一次 app，挑一条放进和评价墙同一张卡。
     有照片 / 视频的优先（一张骑在雪里的照片抵十句好评），其次评分高的。
     哪一个产品没有评价就去掉那一格；全都没有、或者接口不通，整段保持 hidden。 */
  function bfOwners(box) {
    var endpoint = clean(box.getAttribute('data-endpoint'));
    if (!endpoint) return;
    var items = slice(box.querySelectorAll('[data-eb-bf-owner]'));
    var wrap = box.closest('.shopify-section');
    var jobs = items.map(function (it) {
      var id = it.getAttribute('data-eb-bf-owner');
      return fetchJSON(endpoint + '?product=' + encodeURIComponent(id) + '&per=10').then(function (d) {
        var rows = (d && d.reviews) || [];
        if (!rows.length) return false;
        rows = rows.slice().sort(function (a, b) {
          var ma = (a.media || []).length ? 1 : 0, mb = (b.media || []).length ? 1 : 0;
          return (mb - ma) || ((+b.rating || 0) - (+a.rating || 0));
        });
        it.querySelector('[data-eb-bf-owner-card]').innerHTML = card(rows[0], false);
        it.hidden = false;
        return true;
      }).catch(function () { return false; });
    });
    Promise.all(jobs).then(function (got) {
      if (got.some(Boolean)) box.hidden = false;
      else if (wrap) wrap.hidden = true;
    });
  }

  function init(scope) {
    slice((scope || document).querySelectorAll('[data-eb-reviews]')).forEach(function (box) {
      if (box.ebReviewsBound) return;
      box.ebReviewsBound = true;
      wall(box);
    });
    [['[data-eb-review-summary]', summary, 'ebSummaryBound'],
     ['[data-eb-review-topics]', topics, 'ebTopicsBound'],
     ['[data-eb-said][data-endpoint]', changes, 'ebChangesBound'],
     ['[data-eb-bf-owners]', bfOwners, 'ebBfOwnersBound']].forEach(function (row) {
      slice((scope || document).querySelectorAll(row[0])).forEach(function (box) {
        if (box[row[2]]) return;
        box[row[2]] = true;
        row[1](box);
      });
    });
    slice((scope || document).querySelectorAll('[data-eb-account-reviews]')).forEach(function (box) {
      if (box.ebAccountBound) return;
      box.ebAccountBound = true;
      accountForm(box);
    });
    slice((scope || document).querySelectorAll('[data-eb-acc-reviews]')).forEach(function (box) {
      if (box.ebAccBound) return;
      box.ebAccBound = true;
      accWall(box);
    });
    slice((scope || document).querySelectorAll('[data-eb-invite]')).forEach(function (box) {
      if (box.ebInviteBound) return;
      box.ebInviteBound = true;
      invite(box);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', function () { init(); });
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
}());
